<template>
  <div class="mt-2 mt-md-5 mt-lg-4 mt-xl-0">
    <nav-shopping-cart>
      <NuxtPage></NuxtPage>
    </nav-shopping-cart>
  </div>

  <transition name="fade">
    <div
      v-if="isSavingTransaction"
      class="position-fixed top-0 start-0 bg-black bg-opacity-50 w-100 h-100 d-flex justify-content-center align-items-center"
      style="z-index: 1050"
    >
      <loading :loading="isSavingTransaction" color="text-white fs-1" />
    </div>
  </transition>
</template>
<script lang="ts">
import "@/scss/cart/_index.scss";
import { store } from "~/store";
import NavShoppingCart from "@/components/navigation/NavShoppingCart.vue";
import { loadScript } from "@/utils/general";
import { loadScriptPaypal } from "@/utils/paypal";
import { CustomNotifyService } from "@/services/CustomNotifyService";
import Loading from "@/components/ui/Loading.vue";
import eventBus from "@/event-bus";
import seoService from "@/services/SeoService";
import type { NavigationGuardNext, RouteLocationNormalized } from "vue-router";
import auth from "~/services/AuthService";

const notifyService = new CustomNotifyService();
interface StepCodes {
  detail: string;
  payment: string;
  shipment: string;
  "monthly-payment": string;
  review: string;
}

function checkStep(code: keyof StepCodes, to: RouteLocationNormalized) {
  store.commit("checkout/setTransaction", to.params.transactionId || null);
  let backRouteName = null; // ruta de regreso si la validación falla
  const moduleState = to.params.transactionId
    ? "checkoutTransaction"
    : "checkout";
  let activeStep = store.getters[`${moduleState}/getActiveStep`];
  if (!activeStep || activeStep.code != code) {
    store.commit(`${moduleState}/setStep`, code);
    activeStep = store.getters[`${moduleState}/getActiveStep`];
  }
  const params = to.params;
  // Si el usuario no está logueado y no está en el detalle entonces solicita inicio
  if (code != "detail" && !auth.isUserLogged()) {
    store.commit(`${moduleState}/reset`);
    backRouteName = "login";
    // Se omite la validación del detalle si el usuario entra directamente al pago
  } else if (code == "payment") {
    store.commit(
      `${moduleState}/setStep`,
      to.params.transactionId ? "payment" : "detail",
    );
  }
  const prevStep = store.getters[`${moduleState}/getPrevStep`];
  if (!backRouteName) {
    // Si el paso previo no se ha completado regresa al principio
    if (prevStep && !prevStep?.isCompleted && !activeStep?.isCompleted) {
      if (to.params.transactionId) {
        backRouteName = "cart-payment";
      } else if (code != "detail") {
        backRouteName = "cart-detail";
      }
    }
  }
  // Si hay ruta de regreso porque falló la validación, redirige...
  if (backRouteName) {
    return navigateTo({ name: backRouteName, params });
  }
  // Continua su camino porque la validación pasó.
}

export default {
  components: {
    NavShoppingCart,
    Loading,
  },
  setup() {
    definePageMeta({
      middleware: [
        (to, _from) => {
          const map: Record<string, keyof StepCodes> = {
            "cart-detail": "detail",
            "cart-payment": "payment",
            "cart-shipping": "shipment",
            "cart-monthly": "monthly-payment",
            "cart-review": "review",
          };
          if (to.name) {
            return checkStep(map[to.name as string], to);
          }
        },
      ],
    });
  },
  computed: {
    transactionId() {
      return this.$route.params.transactionId
        ? this.$route.params.transactionId
        : null;
    },
    moduleCheckoutState() {
      return this.transactionId ? "checkoutTransaction" : "checkout";
    },
    isSavingTransaction() {
      return store.getters[
        `${this.moduleCheckoutState}/getIsSavingTransaction`
      ];
    },
    isLastStep() {
      return store.getters[`${this.moduleCheckoutState}/getIsLastStep`];
    },
  },
  watch: {
    transactionId(newTransaction) {
      store.commit(
        `checkout/setTransaction`,
        newTransaction.transactionId ? newTransaction.transactionId : null,
      );
    },
  },
  created() {
    seoService.setData({
      title: "Carrito de compras",
      noIndex: true,
    });

    store.commit("setCanCloseMenu", true);
    store.commit(`checkout/setTransaction`, this.transactionId);

    eventBus.on("load-openpay-script", () => {
      this.loadScriptOpenPay();
    });
  },
  mounted() {
    this.loadScriptOpenPay();
    loadScriptPaypal(!!this.transactionId);
    store.commit(`${this.moduleCheckoutState}/setIsSavingTransaction`, false);
  },
  beforeUnmount() {
    eventBus.off("load-openpay-script");
  },
  methods: {
    loadScriptOpenPay() {
      const script1 = "https://js.openpay.mx/openpay.v1.min.js";
      const script2 = "https://js.openpay.mx/openpay-data.v1.min.js";

      // Promesas para los dos scripts
      const scriptPromises = [loadScript(script1), loadScript(script2)];

      // Ejecutar las promesas y validar que ambos scripts se cargaron
      Promise.all(scriptPromises)
        .then(() => {
          console.log("Script Openpay");
          store.dispatch(`${this.moduleCheckoutState}/resetDeviceSessionId`);
        })
        .catch((reason) => {
          console.log("Error:", reason);
          notifyService.getNotification({
            type: "error",
            title: `Ocurrio un error al carga la información`,
            text: `Ocurrio un error al carga la información, favor de recargar la página`,
            time: 3000,
          });
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
